// ─── Insight Categories ─────────────────────────────────────────────────────

export const INSIGHT_CATEGORIES = [
  'skill',
  'hook',
  'claude-md-pattern',
  'mcp-server',
  'prompting-technique',
  'workflow',
  'tool-usage',
  'configuration',
  'general-tip',
] as const;

export type InsightCategory = (typeof INSIGHT_CATEGORIES)[number];

// ─── Source Types ───────────────────────────────────────────────────────────

export const SOURCE_TYPES = [
  'x/twitter',
  'reddit',
  'bluesky',
  'mastodon',
  'github',
  'hackernews',
  'rss',
  'discord',
  'gmail',
  'telegram',
] as const;

export type SourceType = (typeof SOURCE_TYPES)[number];

// ─── Insight Status ─────────────────────────────────────────────────────────

export type InsightStatus = 'published' | 'review' | 'dismissed';

// ─── DynamoDB Entities ──────────────────────────────────────────────────────

export interface Insight {
  insightId: string; // ULID
  sk: 'META';
  title: string;
  summary: string;
  category: InsightCategory;
  tags: string[];
  relevanceScore: number; // 0-10
  status: InsightStatus;
  sourceUrl: string;
  sourceType: SourceType;
  sourceId: string;
  sourceAuthor: string;
  actionableSteps: string[];
  relatedInsightIds: string[];
  s3ContentKey: string;
  s3RawKey: string;
  enrichmentSources?: string[];
  createdAt: string; // ISO 8601
  processedAt: string; // ISO 8601
}

export interface Source {
  sourceId: string;
  sk: 'META';
  type: SourceType;
  name: string;
  identifier: string; // username, subreddit name, repo slug, RSS URL, etc.
  enabled: boolean;
  pollIntervalMinutes: number;
  lastPolledAt: string | null;
  lastError: string | null;
  filters: string[];
  config: Record<string, string>; // source-specific config (e.g., subreddit sort)
  createdAt: string;
  updatedAt: string;
}

// ─── AI Extraction ──────────────────────────────────────────────────────────

export interface ExtractionResult {
  relevanceScore: number;
  category: InsightCategory;
  title: string;
  summary: string;
  actionableSteps: string[];
  tags: string[];
  relatedTopics: string[];
}

// ─── SQS Message Payloads ───────────────────────────────────────────────────

export interface IngestMessage {
  messageType: 'url' | 'text' | 'email';
  sourceType: SourceType;
  sourceId: string;
  sourceUrl: string;
  sourceAuthor: string;
  rawContent: string;
  userNotes?: string;
  submittedAt: string;
  activityId?: string;
  telegramChatId?: number;
}

// ─── API Request/Response Types ─────────────────────────────────────────────

export interface ListInsightsParams {
  category?: InsightCategory;
  sourceType?: SourceType;
  minRelevance?: number;
  status?: InsightStatus;
  limit?: number;
  cursor?: string; // DynamoDB pagination token (base64 encoded)
}

export interface ListInsightsResponse {
  items: Insight[];
  nextCursor: string | null;
  total: number;
}

export interface InsightDetailResponse {
  insight: Insight;
  markdownContent: string;
}

export interface ListSourcesResponse {
  items: Source[];
}

export interface CreateSourceRequest {
  type: SourceType;
  name: string;
  identifier: string;
  pollIntervalMinutes?: number;
  filters?: string[];
  config?: Record<string, string>;
}

export interface UpdateSourceRequest {
  enabled?: boolean;
  pollIntervalMinutes?: number;
  filters?: string[];
  config?: Record<string, string>;
}

export interface SearchRequest {
  query: string;
  category?: InsightCategory;
  minRelevance?: number;
  limit?: number;
}

export interface SearchResponse {
  items: Insight[];
  total: number;
}

export interface ReviewAction {
  action: 'accept' | 'dismiss';
}

export interface StatsResponse {
  totalInsights: number;
  totalSources: number;
  reviewQueueSize: number;
  byCategory: Record<InsightCategory, number>;
  bySource: Record<string, number>;
  recentActivity: { date: string; count: number }[];
}

// ─── Telegram Activity ─────────────────────────────────────────────────────

export type TelegramProcessingStatus =
  | 'received'
  | 'queued'
  | 'processing'
  | 'published'
  | 'review'
  | 'discarded'
  | 'error'
  | 'command';

export interface TelegramActivity {
  activityId: string;
  sk: 'META';
  gsiPk: 'TELEGRAM';
  telegramMessageId: number;
  telegramChatId: number;
  senderName: string;
  messageText: string;
  messageDate: string;
  botReply: string;
  urls: string[];
  userNotes: string;
  messageType: 'url' | 'text' | 'command';
  processingStatus: TelegramProcessingStatus;
  insightIds: string[];
  relevanceScore: number | null;
  aiTitle: string | null;
  aiCategory: string | null;
  aiSummary: string | null;
  processingError: string | null;
  receivedAt: string;
  processedAt: string | null;
}

export interface ListActivityResponse {
  items: TelegramActivity[];
  nextCursor: string | null;
}

// ─── Telegram Webhook ───────────────────────────────────────────────────────

export interface TelegramUpdate {
  update_id: number;
  message?: {
    message_id: number;
    from: { id: number; first_name: string };
    chat: { id: number; type: string };
    date: number;
    text?: string;
    entities?: { type: string; offset: number; length: number; url?: string }[];
  };
}

// ─── Search Index ───────────────────────────────────────────────────────────

export interface SearchIndexEntry {
  id: string;
  title: string;
  summary: string;
  category: InsightCategory;
  tags: string[];
  relevanceScore: number;
  sourceType: SourceType;
  createdAt: string;
}
